import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getSignatureFontById } from '@/data/signatureFonts';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import type { CardCustomization } from '@/stores/cartStore';

interface CartItemCustomizationProps {
  customization: CardCustomization;
  className?: string;
}

// Logical field order for display (matches typical card layout)
const FIELD_ORDER: string[] = [
  'cardtitle', 'idnumber', 'firstname', 'lastname', 'nationality', 
  'dateofbirth', 'placeofbirth', 'height', 'eyecolor', 'haircolor',
  'issuedate', 'expirydate', 'weight', 'sex', 'gender',
  'address', 'city', 'state', 'country', 'zipcode',
  'institution', 'organization', 'major', 'class',
];

// Known compound words that need splitting
const COMPOUND_WORDS: Record<string, string> = {
  'cardtitle': 'Card Title',
  'idnumber': 'ID Number',
  'firstname': 'First Name',
  'lastname': 'Last Name',
  'dateofbirth': 'Date of Birth',
  'placeofbirth': 'Place of Birth',
  'eyecolor': 'Eye Color',
  'haircolor': 'Hair Color',
  'issuedate': 'Issue Date',
  'expirydate': 'Expiry Date',
  'zipcode': 'Zip Code',
};

// Format field key to proper Title Case label
const formatLabel = (key: string): string => {
  // Check for known compound words first
  const normalized = key.toLowerCase().replace(/[_\s]/g, '');
  if (COMPOUND_WORDS[normalized]) {
    return COMPOUND_WORDS[normalized];
  }
  
  // Split by camelCase, underscore, and spaces
  const words = key
    .replace(/([a-z])([A-Z])/g, '$1 $2') // camelCase to spaces
    .replace(/_/g, ' ') // underscores to spaces
    .split(' ')
    .filter(Boolean);
  
  // Capitalize each word for Title Case
  return words.map(word => 
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ).join(' ');
};

// Get sort priority for a field key
const getFieldSortOrder = (key: string): number => {
  const normalized = key.toLowerCase().replace(/[_\s]/g, '');
  const index = FIELD_ORDER.findIndex(orderKey => normalized.includes(orderKey));
  return index >= 0 ? index : FIELD_ORDER.length + 1;
};

export const CartItemCustomization: React.FC<CartItemCustomizationProps> = ({
  customization,
  className,
}) => {
  const [expanded, setExpanded] = useState(false);
  const { t } = useLanguage();
  const { formData, signatureText, signatureFont } = customization;
  const font = getSignatureFontById(signatureFont);

  // Flexible getters for name fields (handle all possible key variations)
  const getFirstName = () => {
    return formData.firstName || formData.first_name || 
           formData.firstname || formData.FirstName || '';
  };

  const getLastName = () => {
    return formData.lastName || formData.last_name || 
           formData.lastname || formData.LastName || '';
  };

  const getDOB = () => {
    return formData.dateOfBirth || formData.date_of_birth || 
           formData.dob || formData.DOB || formData.DateOfBirth || null;
  };

  // Get display name from form data
  const displayName = [getFirstName(), getLastName()]
    .filter(Boolean)
    .join(' ') || 'Not specified';

  // Simply display the date string as-is (free text input)
  const formatDisplayValue = (value: string) => {
    return value || '';
  };

  // Get other relevant fields for compact display
  const dob = getDOB() ? formatDisplayValue(getDOB()!) : null;

  // Keys to hide from cart details (auto-generated/system fields)
  const HIDDEN_PATTERNS = ['mrz', 'qr_code', 'qrcode'];
  
  const isHiddenKey = (key: string): boolean => {
    const keyLower = key.toLowerCase();
    // Hide MRZ and QR fields
    if (HIDDEN_PATTERNS.some(hidden => keyLower.includes(hidden))) return true;
    // Hide linked field copies (keys containing "copy" followed by numbers, e.g., "cardtitle_copy_176...")
    if (/copy[_\s]?\d*/i.test(key)) return true;
    return false;
  };
  
  // Normalize key for deduplication (lowercase, remove underscores/spaces)
  const normalizeKey = (key: string): string => {
    return key.toLowerCase().replace(/[_\s]/g, '');
  };

  // Get all non-empty fields for expanded view, excluding hidden system fields
  // Also deduplicate by normalized key (e.g., idNumber vs idnumber)
  const allFields = useMemo(() => {
    const seenNormalized = new Set<string>();
    const result: { key: string; label: string; value: string }[] = [];
    
    Object.entries(formData)
      .filter(([key, value]) => value && !isHiddenKey(key))
      .forEach(([key, value]) => {
        const normalized = normalizeKey(key);
        if (!seenNormalized.has(normalized)) {
          seenNormalized.add(normalized);
          result.push({
            key,
            label: formatLabel(key),
            value: value,
          });
        }
      });
    
    // Sort by logical field order
    result.sort((a, b) => getFieldSortOrder(a.key) - getFieldSortOrder(b.key));
    
    return result;
  }, [formData]);

  return (
    <div className={cn('text-xs space-y-1', className)}>
      {/* Compact Summary */}
      <div className="space-y-0.5">
        <p className="text-foreground font-medium uppercase">{displayName}</p>
        {dob && <p className="text-muted-foreground uppercase">{dob}</p>}
      </div>

      {/* Expand/Collapse for all details */}
      {allFields.length > 3 && (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="inline-flex items-center gap-1 text-[10px] pt-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors font-medium"
        >
          {expanded ? (
            <>
              <ChevronUp className="h-3 w-3" />
              {t('cart.hideDetails')}
            </>
          ) : (
            <>
              <ChevronDown className="h-3 w-3" />
              {t('cart.yourCardDetails')}
            </>
          )}
        </button>
      )}

      {/* Expanded Details */}
      {expanded && (
        <div className="pt-2 space-y-1.5 border-t border-border/50 mt-2">
          {allFields.map((field, index) => (
            <div key={index} className="grid grid-cols-[auto_1fr] gap-3">
              <span className="text-muted-foreground whitespace-nowrap">{field.label}:</span>
              <span className="text-foreground text-right break-all uppercase">{field.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};